/**
 * O recorte de tempo do financeiro, e o período anterior de mesmo tamanho.
 *
 * Existe porque o Financeiro não tinha nenhum. O filtro de data morava só no
 * Dashboard, e a aba que fala de dinheiro respondia sempre sobre o histórico
 * inteiro — inclusive o painel que eu mesmo escrevi dizendo "realizado no
 * período" sem período nenhum.
 *
 * O ANTERIOR é de mesmo tamanho e termina onde o atual começa. É o que permite
 * dizer "caiu" ou "subiu" sem comparar um mês cheio com meio mês, que é o erro
 * clássico: em 10 de outubro, "este mês" tem dez dias e "mês passado" tem
 * trinta e um, e a comparação direta sempre acusaria queda.
 */

const iso = (d) => d.toISOString().slice(0, 10);
const dia = (v) => (v ? String(v).slice(0, 10) : null);

/** Um dia depois, sem passar por fuso. */
const maisDias = (data, n) => {
  const [y, m, d] = data.split('-').map(Number);
  return iso(new Date(Date.UTC(y, m - 1, d + n)));
};

/** Quantos dias o intervalo cobre, contando os dois extremos. */
export const diasDoPeriodo = (de, ate) => {
  if (!de || !ate) return 0;
  const [y1, m1, d1] = de.split('-').map(Number);
  const [y2, m2, d2] = ate.split('-').map(Number);
  return Math.round((Date.UTC(y2, m2 - 1, d2) - Date.UTC(y1, m1 - 1, d1)) / 86400000) + 1;
};

/**
 * Os recortes que a aba oferece.
 *
 * `tudo` existe e é o padrão: o laboratório tem poucos meses de histórico, e
 * abrir já filtrado esconderia metade do que existe sem avisar. Quando houver
 * dois anos de dado, o padrão passa a ser o mês.
 */
export const PERIODOS = [
  { id: 'tudo', rotulo: 'Tudo' },
  { id: 'mes', rotulo: 'Este mês' },
  { id: 'mes-passado', rotulo: 'Mês passado' },
  { id: 'tres-meses', rotulo: 'Últimos 3 meses' },
  { id: 'ano', rotulo: 'Este ano' },
];

/**
 * De/até de um recorte. `tudo` devolve os dois nulos, que é "sem recorte".
 *
 * `hoje` entra por parâmetro para o teste poder fixar o dia; sem isso a suíte
 * passaria em setembro e falharia em outubro.
 */
export const intervaloDoPeriodo = (id, hoje = new Date()) => {
  const h = iso(hoje);
  const [ano, mes] = h.split('-').map(Number);
  const ultimoDia = (y, m) => new Date(Date.UTC(y, m, 0)).getUTCDate();
  const doMes = (y, m) => ({
    de: `${y}-${String(m).padStart(2, '0')}-01`,
    ate: `${y}-${String(m).padStart(2, '0')}-${String(ultimoDia(y, m)).padStart(2, '0')}`,
  });

  switch (id) {
    case 'mes':
      return doMes(ano, mes);
    case 'mes-passado': {
      const y = mes === 1 ? ano - 1 : ano;
      const m = mes === 1 ? 12 : mes - 1;
      return doMes(y, m);
    }
    case 'tres-meses': {
      // Os três incluem o corrente: setembro devolve julho a setembro.
      const inicio = new Date(Date.UTC(ano, mes - 3, 1));
      return { de: iso(inicio), ate: doMes(ano, mes).ate };
    }
    case 'ano':
      return { de: `${ano}-01-01`, ate: `${ano}-12-31` };
    default:
      return { de: null, ate: null };
  }
};

/**
 * O período anterior, de mesmo tamanho, terminando um dia antes do atual.
 *
 * Sem recorte não há anterior: comparar "tudo" com "tudo antes de tudo" não
 * quer dizer nada, e devolver um intervalo vazio faria a tela exibir uma queda
 * de cem por cento.
 */
export const periodoAnterior = ({ de, ate } = {}) => {
  if (!de || !ate) return { de: null, ate: null };
  const dias = diasDoPeriodo(dia(de), dia(ate));
  const fim = maisDias(dia(de), -1);
  return { de: maisDias(fim, -(dias - 1)), ate: fim };
};

/**
 * Como um número se compara ao do período anterior.
 *
 * `pct` é `null` quando não havia base: crescer a partir de zero não é "mais
 * infinito por cento", é "não havia com o que comparar". Escrever isso, em vez
 * de um número inventado, é o que impede a tela de mentir no primeiro mês.
 */
export const comparacao = (agora, antes) => {
  const a = Math.round((Number(agora) || 0) * 100);
  const b = Math.round((Number(antes) || 0) * 100);
  const delta = (a - b) / 100;

  return {
    delta,
    pct: b === 0 ? null : Math.round((delta / (b / 100)) * 100),
    subiu: delta > 0,
    caiu: delta < 0,
    igual: delta === 0,
    tinhaBase: b !== 0,
  };
};
